import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import React, { useState } from "react";
import { generateBlogFaq, type FAQItem } from "@/lib/generate-blog-faq.functions";
import { useTranslations } from "@/hooks/useTranslations";
import type { Language } from "@/lib/translations";

export type { FAQItem };

interface BlogFAQProps {
  postId: string;
  language: Language;
  initialFaq: FAQItem[] | null;
  onLoaded?: (faq: FAQItem[]) => void;
}

// Renders answer text with support for [label](url) markdown-style links.
const renderAnswer = (text: string) => {
  const parts: React.ReactNode[] = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const [, label = "", url = ""] = match;
    const external = /^https?:\/\//.test(url);
    parts.push(
      <a
        key={`lnk-${key++}`}
        href={url}
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        className="text-primary underline underline-offset-2 hover:text-primary/80"
      >
        {label}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

const BlogFAQ = ({ postId, language, initialFaq, onLoaded }: BlogFAQProps) => {
  const t = useTranslations();
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["blog_faq", postId, language],
    queryFn: async (): Promise<FAQItem[]> => {
      if (initialFaq && initialFaq.length > 0) return initialFaq;
      const data = await generateBlogFaq({ data: { postId, language } });
      const faq = data?.faq ?? [];
      if (faq.length > 0) onLoaded?.(faq);
      return faq;
    },
    staleTime: 1000 * 60 * 60,
  });

  const faq = data ?? initialFaq ?? [];

  if (isError) return null;

  return (
    <section className="mt-16">
      <h2 className="text-2xl md:text-3xl font-bold mb-6 gradient-text">
        {t.blog.faqTitle}
      </h2>

      {isLoading && faq.length === 0 ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-14 rounded-lg bg-muted/30 animate-pulse" />
          ))}
        </div>
      ) : faq.length === 0 ? null : (
        <div className="space-y-3">
          {faq.map((item, idx) => {
            const isOpen = openIdx === idx;
            return (
              <div
                key={idx}
                className="border border-border/60 rounded-lg bg-card/40 backdrop-blur-xs overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(isOpen ? null : idx)}
                  className="w-full flex items-center justify-between text-left px-5 py-4 hover:bg-primary/5 transition-colors"
                  aria-expanded={isOpen}
                >
                  <span className="font-semibold text-foreground pr-4">{item.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-primary shrink-0 transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 text-foreground/80 leading-relaxed">{renderAnswer(item.a)}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default BlogFAQ;
